import React, { useMemo, useState, useEffect } from 'react';
import FlatList from './FlatList';
import MonthCalendar from './MonthCalendar';
import './DaysToggle.sass';
import { ReactComponent as ArrowRight } from '../static/img/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '../static/img/arrow-left.svg';
import { ReactComponent as Calendar } from '../static/img/calendar.svg';
import HospitalLocationFilter from '../components/HospitalLocationFilter';
import moment from 'moment';

export default ({
  days,
  actions,
  showMonth,
  hospitals,
  selectedHospitals,
  setSelectedHospitals,
  setHideTimeSlots,
  hasUserNavigated,
  setHasUserNavigated,
  isMonthView,
  toggleCalendarView,
  onDateSelect,
  timeslotComponent,
}) => {
  const params = new URLSearchParams(window.location.search);
  const date = params.get('date');

  const [selectedDate, setSelectedDate] = useState(() => {
    return date ? moment(date) : null;
  });
  
  useEffect(() => {
    if (selectedDate) return;
    const availableDay = days.find(day => !day.disabled);
    if (availableDay) {
      setSelectedDate(availableDay.item.date)
    }
  }, [selectedDate, days]);

  const currentMonth = useMemo(() => {
    if (days.length > 0) {
      return days[0].item.date.clone().startOf('month');
    } else if (selectedDate) {
      return selectedDate.clone().startOf('month');
    } else if (date) {
      return moment(date).startOf('month');
    }
    return moment().startOf('month');
  }, [selectedDate, days, date]);

  const month = useMemo(() => {
    if (days.length === 0) return '';
    return currentMonth.format('MMMM');
  }, [currentMonth]);

  const daysForSelectedHospitals = useMemo(() => {
    let filteredDays = days.map(day => {
      let daysForSelectHospitalsFilter = [];
      for (let i = 0; i < day.item.clinic?.locations?.length; i++) {
        if (day.item.clinic !== undefined) {
          daysForSelectHospitalsFilter.push(day.item.clinic?.locations[i]);
        }
      }
      const selectedHospitalInDay = daysForSelectHospitalsFilter.some(location =>
        selectedHospitals.includes(location),
      );
      if (day.item.clinic && selectedHospitalInDay) {
        return { ...day, disabled: false };
      } else if (day.item.clinic && !selectedHospitalInDay) {
        return { ...day, disabled: true };
      }
      return day;
    });
    return filteredDays;
  }, [days, selectedHospitals]);

  useEffect(() => {
        daysForSelectedHospitals.filter(slot => slot.disabled === false).length > 0
      ? setHideTimeSlots(false)
      : setHideTimeSlots(true);
  }, [daysForSelectedHospitals, setHideTimeSlots])

  return (
    <>
      <HospitalLocationFilter
        hospitals={hospitals}
        selectedHospitals={selectedHospitals}
        setSelectedHospitals={setSelectedHospitals}
      />
      {showMonth && (
        <div className="month-navigation__wrapper">
          {actions && (
            <button
              className="prev-month__btn"
              onClick={e => {
                if (e.isTrusted) {
                  setHasUserNavigated(true);
                }
                actions.prevMonth();
              }}
              aria-label="Previous month"
            >
              <ArrowLeft />
            </button>
          )}
          <div className="month-name">{month}</div>
          {actions && (
            <button
              className="next-month__btn"
              onClick={e => {
                if (e.isTrusted) {
                  setHasUserNavigated(true);
                }
                actions.nextMonth();
              }}
              aria-label="Next month"
            >
              <ArrowRight />
            </button>
          )}
          <button
            className="calendar-toggle"
            onClick={toggleCalendarView}
            aria-label={isMonthView ? 'Switch to Week view' : 'Switch to Month view'}
          >
            <Calendar />
            {isMonthView ? 'Week view' : 'Month view'}
          </button>
        </div>
      )}
      {!isMonthView ? (
        <div className="days-toggle__outer-wrapper">
          {actions && (
            <button
              className="prev-period__btn"
              onClick={e => {
                if (e.isTrusted) {
                  setHasUserNavigated(true);
                }
                actions.prev();
              }}
              aria-label="Previous week"
            >
              <ArrowLeft />
            </button>
          )}
          <div className="days-toggle__scroll">
            <div className="days-toggle__wrapper">
              <FlatList
                data={daysForSelectedHospitals}
                renderItem={({ item: { date }, disabled, select }, index) => {
                  // Use date-based selection instead of index-based
                  const isDateSelected =
                    selectedDate && selectedDate.isSame(date, 'day') && !disabled;

                  return (
                    <button
                      type="button"
                      className={`day-toggle__button ${isDateSelected ? 'm--active' : ''} ${disabled ? 'm--disabled' : ''}`}
                      key={index}
                      disabled={disabled}
                      aria-label={date.format('dddd D MMM YYYY')}
                      onClick={e => {
                        if (e.isTrusted) {
                          setHasUserNavigated(true);
                        }
                        if (!disabled) {
                          setSelectedDate(date);
                          if (select) {
                            select();
                          }
                          if (onDateSelect) {
                            onDateSelect(date.format('YYYY-MM-DD'));
                          }
                        }
                      }}
                    >
                      {date.format('ddd')}
                      <br />
                      {date.format('D MMM')}
                    </button>
                  );
                }}
              />
            </div>
          </div>
          {actions && (
            <button
              className="next-period__btn"
              onClick={e => {
                if (e.isTrusted) {
                  setHasUserNavigated(true);
                }
                actions.next();
              }}
              aria-label="Next week"
            >
              <ArrowRight />
            </button>
          )}
        </div>
      ) : (
        <div className="month-view__wrapper">
          <MonthCalendar
            days={daysForSelectedHospitals}
            selectedDate={selectedDate}
            currentMonth={currentMonth}
            onDateSelect={dayData => {
              setSelectedDate(dayData.item.date);
              if (dayData.select) {
                dayData.select();
              }
              if (onDateSelect) {
                onDateSelect(dayData.item.date.format('YYYY-MM-DD'));
              }
            }}
          />
          <div className="month-view__timeslots">
            {selectedDate && daysForSelectedHospitals.some(day => !day.disabled) && (
              <p className="month-view__timeslots-header">
                Available times on {selectedDate.format('dddd Do MMMM')}
              </p>
            )}
            <div className="month-view__timeslots-content">{timeslotComponent}</div>
          </div>
        </div>
      )}
    </>
  );
};
